import { z } from 'zod'

import { searchTextParamSchema } from '@/schemas/common'
import { pageSizeSchema } from '@/schemas/table'

/**
 * URL building blocks shared by the Courses routes.
 *
 * Each route's own schema lives in its `<Screen>.loader.ts` and is exported
 * from there as the route's `validateSearch` too (docs/route-loading.md,
 * "Search params"), so the loader and the route never read one URL two ways.
 *
 * EVERY FIELD FALLS BACK instead of throwing (`.catch`): these params are
 * tabs, filters and pages over one screen, so a hand-edited or stale value
 * lands on the default view rather than an error page.
 *
 * CANONICAL URLS: a param equal to its default is not written. Each route
 * passes its `…_SEARCH_DEFAULTS` to TanStack's `stripSearchParams`
 * middleware, which drops it from every URL the app builds — a tab click
 * back to Lectures leaves `/courses/17`, not `/courses/17?tab=lectures`.
 *
 * No `createFileRoute` here, so a test may import this module.
 */

/** A 1-based page. */
export const pageParamSchema = z.number().int().min(1).default(1).catch(1)

/** The opening page size for a server-paged list here (the dictionary). */
export const DEFAULT_PAGE_SIZE = 20

/** The app's paging contract (`schemas/table.ts`): 10 / 20 / 50 / 100. */
export const pageSizeParamSchema = pageSizeSchema
  .default(DEFAULT_PAGE_SIZE)
  .catch(DEFAULT_PAGE_SIZE)

/**
 * Free text — a dictionary search. Trimmed, and blank is ABSENT, so `?q=`,
 * `?q=%20` and no `q` are one URL meaning "everything".
 */
export const queryTextParamSchema = searchTextParamSchema
  .transform((text) => text.trim())
  .pipe(z.string().min(1))
  .optional()
  .catch(undefined)
